import React, { useCallback, useEffect, useState, useRef } from 'react';
interface LocationDot {
  id: string;
  name: string;
  x: number; // percentage from left (0-100)
  y: number; // percentage from top (0-100)
}
interface InteractiveMapProps {
  locations: LocationDot[];
  mapSrc: string;
}
interface Transform {
  x: number;
  y: number;
  scale: number;
}
interface TooltipState {
  visible: boolean;
  name: string;
  screenX: number;
  screenY: number;
}
export function InteractiveMap({
  locations,
  mapSrc
}: InteractiveMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [transform, setTransform] = useState<Transform>({
    x: 0,
    y: 0,
    scale: 1
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0
  });
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    name: '',
    screenX: 0,
    screenY: 0
  });
  const [isHovering, setIsHovering] = useState(false);
  const MIN_SCALE = 0.8;
  const MAX_SCALE = 5;
  const HIGHLIGHT_COLOR = '#061D76';
  const DOT_DETECTION_RADIUS = 3;
  const HOVER_CIRCLE_SIZE = 120;
  // Load SVG content
  useEffect(() => {
    fetch(mapSrc).then(res => res.text()).then(text => {
      // Ensure the SVG fits the container by setting width/height to 100%
      // and preserveAspectRatio to show the full map
      let modifiedSvg = text;
      modifiedSvg = modifiedSvg.replace(/<svg([^>]*)>/, (match, attrs) => {
        // Remove any existing width/height attributes
        let cleanAttrs = attrs.replace(/\s*width\s*=\s*["'][^"']*["']/gi, '').replace(/\s*height\s*=\s*["'][^"']*["']/gi, '');
        return `<svg${cleanAttrs} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">`;
      });
      setSvgContent(modifiedSvg);
    }).catch(err => console.error('Failed to load SVG:', err));
  }, [mapSrc]);
  // Find the closest SVG element to a location and highlight it
  const highlightLocationDots = useCallback(() => {
    const svgContainer = svgContainerRef.current;
    if (!svgContainer || !svgContent) return;
    const svg = svgContainer.querySelector('svg');
    if (!svg) return;
    // Get SVG viewBox dimensions
    const viewBox = svg.getAttribute('viewBox');
    let svgWidth = 1000,
      svgHeight = 500;
    if (viewBox) {
      const parts = viewBox.split(' ').map(Number);
      if (parts.length === 4) {
        svgWidth = parts[2];
        svgHeight = parts[3];
      }
    }
    // Get ALL shape elements - circles, ellipses, paths, and rects
    const allElements = svg.querySelectorAll('circle, ellipse, path, rect');
    // Reset all elements
    allElements.forEach(el => {
      const svgEl = el as SVGElement;
      svgEl.classList.remove('highlighted-dot');
      svgEl.removeAttribute('data-location-id');
      svgEl.removeAttribute('data-location-name');
      svgEl.style.fill = '';
      svgEl.style.cursor = '';
      svgEl.style.transition = '';
    });
    // Helper: get center coordinates of any SVG element in SVG user space
    const getElementCenter = (el: SVGElement): {
      cx: number;
      cy: number;
    } | null => {
      try {
        // Parse transform="translate(x y)" from element and all parent groups
        let totalX = 0;
        let totalY = 0;
        let current: Element | null = el;
        // Walk up the tree collecting translate transforms
        while (current && current !== svg) {
          const transformAttr = current.getAttribute('transform');
          if (transformAttr) {
            const translateMatch = transformAttr.match(/translate\(\s*([-\d.]+)[\s,]+([-\d.]+)\s*\)/);
            if (translateMatch) {
              totalX += parseFloat(translateMatch[1]);
              totalY += parseFloat(translateMatch[2]);
            }
          }
          current = current.parentElement;
        }
        // Get the element's own position attributes
        const tagName = el.tagName.toLowerCase();
        let baseCx = 0;
        let baseCy = 0;
        if (tagName === 'circle' || tagName === 'ellipse') {
          baseCx = parseFloat(el.getAttribute('cx') || '0');
          baseCy = parseFloat(el.getAttribute('cy') || '0');
        } else if (tagName === 'rect') {
          const w = parseFloat(el.getAttribute('width') || '0');
          const h = parseFloat(el.getAttribute('height') || '0');
          baseCx = parseFloat(el.getAttribute('x') || '0') + w / 2;
          baseCy = parseFloat(el.getAttribute('y') || '0') + h / 2;
        } else {
          return null;
        }
        return {
          cx: baseCx + totalX,
          cy: baseCy + totalY
        };
      } catch (e) {
        return null;
      }
    };
    // Build a list of dot-like elements with their centers
    const dotElements: {
      el: SVGElement;
      cx: number;
      cy: number;
    }[] = [];
    allElements.forEach(el => {
      // Only process circle, ellipse, and small paths/rects
      const tagName = el.tagName.toLowerCase();
      if (tagName === 'g' || tagName === 'defs' || tagName === 'style') return;
      const center = getElementCenter(el as SVGElement);
      if (center) {
        dotElements.push({
          el: el as SVGElement,
          ...center
        });
      }
    });
    // For each location, find the nearest dot element and highlight it
    const threshold = DOT_DETECTION_RADIUS / 100 * Math.max(svgWidth, svgHeight);
    locations.forEach(loc => {
      const targetX = loc.x / 100 * svgWidth;
      const targetY = loc.y / 100 * svgHeight;
      let nearestDot: SVGElement | null = null;
      let nearestDist = Infinity;
      dotElements.forEach(({
        el,
        cx,
        cy
      }) => {
        const dist = Math.sqrt((cx - targetX) ** 2 + (cy - targetY) ** 2);
        if (dist < threshold && dist < nearestDist) {
          nearestDist = dist;
          nearestDot = el;
        }
      });
      if (nearestDot) {
        ;
        (nearestDot as SVGElement).style.fill = HIGHLIGHT_COLOR;
        (nearestDot as SVGElement).style.transition = 'fill 0.3s ease';
        nearestDot.classList.add('highlighted-dot');
        nearestDot.setAttribute('data-location-id', loc.id);
        nearestDot.setAttribute('data-location-name', loc.name);
        // Make highlighted dots slightly larger for visibility
        nearestDot.style.cursor = 'pointer';
      }
    });
    console.log(`Map: Found ${dotElements.length} dot elements, highlighted ${locations.length} locations`);
  }, [locations, svgContent]);
  // Apply highlighting when SVG loads or locations change
  useEffect(() => {
    if (svgContent) {
      // Small delay to ensure SVG is rendered
      setTimeout(highlightLocationDots, 100);
    }
  }, [svgContent, highlightLocationDots]);
  // Disable page scroll when hovering over map
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const preventScroll = (e: WheelEvent) => {
      if (isHovering) {
        e.preventDefault();
      }
    };
    // Use passive: false to allow preventDefault
    window.addEventListener('wheel', preventScroll, {
      passive: false
    });
    return () => {
      window.removeEventListener('wheel', preventScroll);
    };
  }, [isHovering]);
  // Handle wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, transform.scale * zoomFactor));
    // Zoom toward mouse position
    const newX = mouseX - (mouseX - transform.x) * (newScale / transform.scale);
    const newY = mouseY - (mouseY - transform.y) * (newScale / transform.scale);
    setTransform({
      x: newX,
      y: newY,
      scale: newScale
    });
  }, [transform]);
  // Mouse down - start drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - transform.x,
      y: e.clientY - transform.y
    });
  }, [transform]);
  // Mouse move - drag or hover for tooltip
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;
    if (isDragging) {
      setTransform(prev => ({
        ...prev,
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      }));
      setTooltip(prev => ({
        ...prev,
        visible: false
      }));
    } else {
      // Check if hovering over a highlighted dot
      const target = e.target as SVGElement;
      if (target.classList.contains('highlighted-dot')) {
        const name = target.getAttribute('data-location-name');
        if (name) {
          const rect = container.getBoundingClientRect();
          setTooltip({
            visible: true,
            name,
            screenX: e.clientX - rect.left,
            screenY: e.clientY - rect.top - 50
          });
        }
      } else {
        setTooltip(prev => ({
          ...prev,
          visible: false
        }));
      }
    }
  }, [isDragging, dragStart]);
  // Mouse up - end drag
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  // Mouse enter/leave for scroll control
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setIsDragging(false);
    setTooltip(prev => ({
      ...prev,
      visible: false
    }));
  }, []);
  // Click on highlighted dot to zoom
  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as SVGElement;
    if (target.classList.contains('highlighted-dot')) {
      const container = containerRef.current;
      const svgContainer = svgContainerRef.current;
      if (!container || !svgContainer) return;
      const svg = svgContainer.querySelector('svg');
      if (!svg) return;
      const containerRect = container.getBoundingClientRect();
      const svgRect = svg.getBoundingClientRect();
      // Get dot position relative to container
      const dotRect = target.getBoundingClientRect();
      const dotCenterX = dotRect.left + dotRect.width / 2 - svgRect.left;
      const dotCenterY = dotRect.top + dotRect.height / 2 - svgRect.top;
      // Calculate position relative to current transform
      const relX = dotCenterX / svgRect.width * containerRect.width;
      const relY = dotCenterY / svgRect.height * containerRect.height;
      const newScale = 2.5;
      const newX = containerRect.width / 2 - relX * newScale;
      const newY = containerRect.height / 2 - relY * newScale;
      setTransform({
        x: newX,
        y: newY,
        scale: newScale
      });
      const name = target.getAttribute('data-location-name');
      if (name) {
        setTooltip({
          visible: true,
          name,
          screenX: containerRect.width / 2,
          screenY: containerRect.height / 2 - 50
        });
      }
    }
  }, []);
  // Reset zoom
  const handleReset = useCallback(() => {
    setTransform({
      x: 0,
      y: 0,
      scale: 1
    });
    setTooltip(prev => ({
      ...prev,
      visible: false
    }));
  }, []);
  return <div ref={containerRef} className="relative w-full overflow-hidden" style={{
    aspectRatio: '2/1',
    cursor: isDragging ? 'grabbing' : 'grab'
  }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={() => {
    setIsDragging(false);
    setTooltip(prev => ({
      ...prev,
      visible: false
    }));
  }} onClick={handleClick} data-id="element-320">
      {/* SVG Container with transform */}
      <div ref={svgContainerRef} className="w-full h-full" style={{
      transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
      transformOrigin: '0 0',
      transition: isDragging ? 'none' : 'transform 0.1s ease-out'
    }} dangerouslySetInnerHTML={{
      __html: svgContent
    }} data-id="element-321" />

      {/* Tooltip overlay - Large circle with name inside */}
      {tooltip.visible && <div className="absolute pointer-events-none" style={{
      left: tooltip.screenX,
      top: tooltip.screenY,
      transform: 'translate(-50%, -50%)',
      zIndex: 20
    }} data-id="element-322">
          <div className="rounded-full bg-[#061D76] flex items-center justify-center shadow-xl" style={{
        width: `${HOVER_CIRCLE_SIZE}px`,
        height: `${HOVER_CIRCLE_SIZE}px`
      }} data-id="element-323">
            <span className="text-white text-base md:text-lg font-medium whitespace-nowrap" data-id="element-324">
              {tooltip.name}
            </span>
          </div>
        </div>}

      {/* Zoom controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-10" data-id="element-325">
        <button onClick={e => {
        e.stopPropagation();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const newScale = Math.min(MAX_SCALE, transform.scale * 1.3);
        const newX = centerX - (centerX - transform.x) * (newScale / transform.scale);
        const newY = centerY - (centerY - transform.y) * (newScale / transform.scale);
        setTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }} className="w-8 h-8 bg-white rounded shadow-md flex items-center justify-center text-[#061D76] font-bold text-lg hover:bg-gray-50 transition-colors" aria-label="Zoom in" data-id="element-326">
          +
        </button>
        <button onClick={e => {
        e.stopPropagation();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const newScale = Math.max(MIN_SCALE, transform.scale * 0.7);
        const newX = centerX - (centerX - transform.x) * (newScale / transform.scale);
        const newY = centerY - (centerY - transform.y) * (newScale / transform.scale);
        setTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }} className="w-8 h-8 bg-white rounded shadow-md flex items-center justify-center text-[#061D76] font-bold text-lg hover:bg-gray-50 transition-colors" aria-label="Zoom out" data-id="element-327">
          −
        </button>
        {transform.scale !== 1 && <button onClick={e => {
        e.stopPropagation();
        handleReset();
      }} className="w-8 h-8 bg-white rounded shadow-md flex items-center justify-center text-[#061D76] text-xs font-semibold hover:bg-gray-50 transition-colors" aria-label="Reset zoom" data-id="element-328">
            ↺
          </button>}
      </div>

      {/* Scroll hint */}
      {isHovering && transform.scale === 1 && <div className="absolute bottom-4 left-4 text-xs text-[#061D76]/60 bg-white/80 px-2 py-1 rounded" data-id="element-329">
          Scroll to zoom • Drag to pan
        </div>}
    </div>;
}